import type { NormalizedRoundSchedule, RoundPersonSummary } from "../../domain/round/types";
import type { RoundStaffSummary } from "../../lib/api";

export type ManualBoardPersonOption = {
  personId: string;
  displayName: string;
  baseName: string;
  driverEligible: boolean;
  transportIncluded: boolean;
  fromStaff: boolean;
  fromSchedule: boolean;
};

export type ManualBoardPoolGroup = {
  baseName: string;
  people: ManualBoardPersonOption[];
};

export type ManualBoardPersonImpact = {
  personId: string;
  displayName: string;
  holesBefore: number;
  holesAfter: number;
  holesDelta: number;
  longestBreakBefore: number;
  longestBreakAfter: number;
  dutyWindowBefore: number;
  dutyWindowAfter: number;
  assignmentCountBefore: number;
  assignmentCountAfter: number;
  assignmentChanged: boolean;
};

export type ManualBoardComparison = {
  changedPeople: ManualBoardPersonImpact[];
  changedAssignments: number;
  peopleWithMoreHoles: number;
  peopleWithFewerHoles: number;
  peopleAt27Plus: number;
  unassignedCount: number;
  maxBreakMinutes: number;
};

function normalize(value: string) {
  return value.trim().toLocaleLowerCase("pl");
}

function dutyWindow(person: RoundPersonSummary | undefined) {
  if (!person || person.dutyStartMinutes == null || person.dutyEndMinutes == null) return 0;
  return Math.max(0, person.dutyEndMinutes - person.dutyStartMinutes);
}

function assignmentPlaces(schedule: NormalizedRoundSchedule | null | undefined, personId: string) {
  if (!schedule) return [] as string[];
  return schedule.assignments
    .filter((assignment) => assignment.personId === personId)
    .map((assignment) => `${assignment.groupId}:${assignment.segment}`)
    .sort();
}

export function buildManualBoardPeople(
  staff: RoundStaffSummary[],
  schedule: NormalizedRoundSchedule | null | undefined,
): ManualBoardPersonOption[] {
  const byId = new Map<string, ManualBoardPersonOption>();

  for (const person of staff) {
    if (person.role !== "scorer" && !schedule?.assignments.some((assignment) => assignment.personId === person.personId)) continue;
    byId.set(person.personId, {
      personId: person.personId,
      displayName: person.displayName,
      baseName: person.baseName || "Brak noclegu",
      driverEligible: person.driverEligible,
      transportIncluded: person.transportIncluded,
      fromStaff: true,
      fromSchedule: false,
    });
  }

  for (const person of schedule?.people || []) {
    const current = byId.get(person.personId);
    if (current) {
      current.fromSchedule = true;
      if (!current.displayName) current.displayName = person.displayName;
      current.driverEligible = person.driverEligible || current.driverEligible;
      current.transportIncluded = person.transportIncluded;
      continue;
    }
    byId.set(person.personId, {
      personId: person.personId,
      displayName: person.displayName || person.personId,
      baseName: "Z rozpiski",
      driverEligible: person.driverEligible,
      transportIncluded: person.transportIncluded,
      fromStaff: false,
      fromSchedule: true,
    });
  }

  return [...byId.values()].sort((a, b) => a.displayName.localeCompare(b.displayName, "pl"));
}

export function buildUnassignedPool(
  people: ManualBoardPersonOption[],
  schedule: NormalizedRoundSchedule | null | undefined,
): ManualBoardPoolGroup[] {
  const assigned = new Set((schedule?.assignments || []).map((assignment) => assignment.personId));
  const groups = new Map<string, ManualBoardPersonOption[]>();

  for (const person of people) {
    if (assigned.has(person.personId)) continue;
    const key = person.baseName || "Brak noclegu";
    const rows = groups.get(key) || [];
    rows.push(person);
    groups.set(key, rows);
  }

  return [...groups.entries()]
    .map(([baseName, rows]) => ({
      baseName,
      people: rows.sort((a, b) => a.displayName.localeCompare(b.displayName, "pl")),
    }))
    .sort((a, b) => a.baseName.localeCompare(b.baseName, "pl"));
}

export function compareManualBoardSchedules(
  baseline: NormalizedRoundSchedule | null | undefined,
  current: NormalizedRoundSchedule | null | undefined,
  nameById: Map<string, string>,
): ManualBoardComparison {
  if (!current) {
    return {
      changedPeople: [],
      changedAssignments: 0,
      peopleWithMoreHoles: 0,
      peopleWithFewerHoles: 0,
      peopleAt27Plus: 0,
      unassignedCount: 0,
      maxBreakMinutes: 0,
    };
  }

  const beforeById = new Map((baseline?.people || []).map((person) => [person.personId, person]));
  const afterById = new Map(current.people.map((person) => [person.personId, person]));
  const ids = new Set<string>([
    ...beforeById.keys(),
    ...afterById.keys(),
    ...(baseline?.assignments || []).map((assignment) => assignment.personId),
    ...current.assignments.map((assignment) => assignment.personId),
  ]);

  const changedPeople: ManualBoardPersonImpact[] = [];
  for (const personId of ids) {
    const before = beforeById.get(personId);
    const after = afterById.get(personId);
    const beforePlaces = assignmentPlaces(baseline, personId);
    const afterPlaces = assignmentPlaces(current, personId);
    const assignmentChanged = beforePlaces.join("|") !== afterPlaces.join("|");
    const holesBefore = before?.holes || 0;
    const holesAfter = after?.holes || 0;
    const longestBreakBefore = before?.longestBreakMinutes || 0;
    const longestBreakAfter = after?.longestBreakMinutes || 0;
    const dutyWindowBefore = dutyWindow(before);
    const dutyWindowAfter = dutyWindow(after);

    if (!assignmentChanged && holesBefore === holesAfter && longestBreakBefore === longestBreakAfter && dutyWindowBefore === dutyWindowAfter) continue;

    changedPeople.push({
      personId,
      displayName: nameById.get(personId) || after?.displayName || before?.displayName || personId,
      holesBefore,
      holesAfter,
      holesDelta: holesAfter - holesBefore,
      longestBreakBefore,
      longestBreakAfter,
      dutyWindowBefore,
      dutyWindowAfter,
      assignmentCountBefore: beforePlaces.length,
      assignmentCountAfter: afterPlaces.length,
      assignmentChanged,
    });
  }

  changedPeople.sort((a, b) => {
    const burdenA = Math.abs(a.holesDelta) * 1000 + Math.abs(a.longestBreakAfter - a.longestBreakBefore) + Math.abs(a.dutyWindowAfter - a.dutyWindowBefore);
    const burdenB = Math.abs(b.holesDelta) * 1000 + Math.abs(b.longestBreakAfter - b.longestBreakBefore) + Math.abs(b.dutyWindowAfter - b.dutyWindowBefore);
    return burdenB - burdenA || a.displayName.localeCompare(b.displayName, "pl");
  });

  const assignedIds = new Set(current.assignments.map((assignment) => assignment.personId));
  const currentPersonIds = new Set(current.people.map((person) => person.personId));
  const unassignedCount = [...currentPersonIds].filter((personId) => !assignedIds.has(personId)).length;

  const beforeAssignments = new Set((baseline?.assignments || []).map((assignment) => `${assignment.personId}:${assignment.groupId}:${assignment.segment}`));
  const afterAssignments = new Set(current.assignments.map((assignment) => `${assignment.personId}:${assignment.groupId}:${assignment.segment}`));
  let changedAssignments = 0;
  for (const value of beforeAssignments) if (!afterAssignments.has(value)) changedAssignments += 1;
  for (const value of afterAssignments) if (!beforeAssignments.has(value)) changedAssignments += 1;

  return {
    changedPeople,
    changedAssignments,
    peopleWithMoreHoles: changedPeople.filter((person) => person.holesDelta > 0).length,
    peopleWithFewerHoles: changedPeople.filter((person) => person.holesDelta < 0).length,
    peopleAt27Plus: current.people.filter((person) => person.holes >= 27).length,
    unassignedCount,
    maxBreakMinutes: Math.max(0, ...current.people.map((person) => person.longestBreakMinutes || 0)),
  };
}

export function filterManualBoardPeople(people: ManualBoardPersonOption[], query: string) {
  const q = normalize(query);
  if (!q) return people;
  return people.filter((person) => normalize(`${person.displayName} ${person.baseName}`).includes(q));
}
